import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Session } from '@supabase/supabase-js'
import { isSupabaseConfigured, supabase } from '@/lib/supabase'

export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null)
  const ready = ref(false)
  const busy = ref(false)
  const error = ref<string | null>(null)
  // null означает «ещё не спрашивали». Кешируем ответ, чтобы не ходить в RPC
  // на каждый переход по роутеру. Сброс висит на onAuthStateChange, а он
  // срабатывает не только на вход и выход, но и на обновление токена, — значит
  // кеш живёт до ближайшего события авторизации, а не всю сессию.
  const isAdmin = ref<boolean | null>(null)

  async function checkAdmin(): Promise<boolean> {
    if (!session.value) {
      isAdmin.value = false
      return false
    }
    if (isAdmin.value !== null) return isAdmin.value

    const { data, error: rpcError } = await supabase.rpc('is_admin')
    if (rpcError) {
      // Сбой проверки трактуем как отказ: пустить внутрь по ошибке хуже,
      // чем показать отказ и дать войти заново.
      console.warn('Не удалось проверить права:', rpcError.message)
      isAdmin.value = false
      return false
    }

    isAdmin.value = data === true
    return isAdmin.value
  }

  /** Поднимает сессию из localStorage и подписывается на её изменения. */
  async function init() {
    if (ready.value) return
    if (!isSupabaseConfigured) {
      ready.value = true
      return
    }
    const { data } = await supabase.auth.getSession()
    session.value = data.session
    supabase.auth.onAuthStateChange((_event, next) => {
      session.value = next
      // Сессия сменилась — прежний ответ про права больше не действителен.
      isAdmin.value = null
    })
    ready.value = true
  }

  async function signIn(email: string, password: string) {
    busy.value = true
    error.value = null
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password })
    busy.value = false
    if (err) {
      error.value = err.message
      return false
    }
    session.value = data.session
    return true
  }

  async function signOut() {
    await supabase.auth.signOut()
    session.value = null
    isAdmin.value = null
  }

  return { session, ready, busy, error, isAdmin, init, signIn, signOut, checkAdmin }
})
